import * as _ from 'lodash';
import * as moment from 'moment';
import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { IPostTransplantRejection, IRecipientProfilePostTransplantData } from '@nkr/models/index';
import { IValidation } from './validation';
import { FileDocumentType } from '@nkr/enums/document-type';
import AdminOnly from './admin-only';
import DonorDocuments from './donor-documents';
import KidneyForLife from './kidney-for-life';
import Details from './details';
import PatientPrivacy from './patient-privacy';
import RejectionEpisodeModal from './graft-failure-rejection/rejection-episode-modal';

interface IProps {
    centerId: number,
    recipientId: number,
    lookupData: any,
    data: IRecipientProfilePostTransplantData,
    rejectionEpisodeList: IPostTransplantRejection[],
    isAdmin: boolean,
    validation: IValidation,
    onChange: (data: IRecipientProfilePostTransplantData) => void,
    onViewFile: (docType: FileDocumentType) => void,
    onGetFinancialInfo: (donorId: number) => void,
    onSaveRejectionEpisodeDraft: (rejection: IPostTransplantRejection) => void,
    onCreateRejectionEpisode: (rejection: IPostTransplantRejection) => void,
    onUpdateRejectionEpisode: (rejection: IPostTransplantRejection) => void,
    onDeleteRejectionEpisode: (rejectionId: number) => void,
    onUnDeleteRejectionEpisode: (rejectionId: number) => void
}

let cleanPostTransplantObject: IPostTransplantRejection = {
    id: 0,
    recipientId: 0,
    dateOfRejection: null,
    deNovoDsaPresent: null,
    persistentDsaPresent: null,
    deNovoDsaNonHla: null,
    persistentDsaNonHla: null,
    persistentDsaOther: null,
    persistentDsaOtherDetails: '',
    wasBiopsyPerformed: null,
    treatments: [],
    dsas: [],
    biopsy: null,
    isDeleted: false,
    isDraft: false,
}

const Content = (props: IProps) => {
    const [showRejectionEpisode, toggleRejectionModal] = useState<boolean>(false);
    const [rejectionModalEditMode, setRejectionModalEditMode] = useState<boolean>(false);
    const [editableRejection, setEditableRejection] = useState<IPostTransplantRejection>(cleanPostTransplantObject);

    const handleDataUpdate = (data: IRecipientProfilePostTransplantData) => {
        props.onChange(data);
    };

    const handlePatientPrivacyChange = (name: string, value: any) => {
        let data = _.cloneDeep(props.data);

        if (name == 'isWillingToMeetDonor') {
            data.isWillingToMeetDonor = value;
        }

        if (name == 'isSharingGfAndCreatinine') {
            data.isSharingGfAndCreatinine = value;
        }

        props.onChange(data);
    };

    const handleEditRejectionEpisode = (id: number) => {
        let episode = props.rejectionEpisodeList.find(x => x.id == id);

        if (episode != null) {
            setEditableRejection(prevState => episode);
            toggleRejectionModal(prevState => true);
            setRejectionModalEditMode(prevState => true);
        }
    }

    const handleAddRejection = () => {
        setEditableRejection(cleanPostTransplantObject);
        toggleRejectionModal(prevState => true);
        setRejectionModalEditMode(prevState => false);
    }

    const handleCloseRejection = () => {
        setEditableRejection(cleanPostTransplantObject);
        setRejectionModalEditMode(prevState => false);
        toggleRejectionModal(prevState => false);
    }

    let { data, lookupData } = props;

    if (data == null || lookupData == null) {
        return null;
    }

    let showKidneyForLife =
        props.data.transplantedDonor
        && props.data.transplantedDonor.histogeneticsRawTypingFile.length > 1
        && props.data.histogeneticsRawTypingFile.length > 1;

    return (
        <div style={{ padding: 15 }}>
            <Row>
                <Col xs={6}>
                    <h3 style={{ marginTop: 0, marginBottom: 20 }}>
                        Transplant Date: {props.data.transplantDate != null ? moment(props.data.transplantDate).format('MM/DD/YYYY') : ''}
                    </h3>
                </Col>
            </Row>

            {props.isAdmin && (
                <AdminOnly
                    data={props.data}
                    validation={props.validation}
                    swapList={props.lookupData.swapList}
                    transportMethodList={props.lookupData.transportMethodList}
                    logisticsProviderList={props.lookupData.logisticsList}
                    onChange={handleDataUpdate} />
            )}

            <PatientPrivacy
                isWillingToMeetDonor={props.data.isWillingToMeetDonor}
                isSharingGfAndCreatinine={props.data.isSharingGfAndCreatinine}
                onChange={handlePatientPrivacyChange}
            />

            <DonorDocuments
                centerId={props.centerId}
                recipientId={props.recipientId}
                data={props.data}
                onViewFile={props.onViewFile}
                onGetFinancialInfo={props.onGetFinancialInfo} />

            {showKidneyForLife && (
                <KidneyForLife
                    data={props.data}
                    onChange={handleDataUpdate} />
            )}

            <Details
                data={props.data}
                rejectionEpisodeList={props.rejectionEpisodeList}
                validation={props.validation}
                donorRelationList={props.lookupData.donorRelationList}
                desensitizationList={props.lookupData.desensitizationList}
                graftFailureReasonList={props.lookupData.graftFailureReasonList}
                onChange={handleDataUpdate}
                onAddRejectionEpisode={handleAddRejection}
                onEditRejectionEpisode={handleEditRejectionEpisode}
                onDeleteRejectionEpisode={props.onDeleteRejectionEpisode}
                onUnDeleteRejectionEpisode={props.onUnDeleteRejectionEpisode}
            />

            <RejectionEpisodeModal
                show={showRejectionEpisode}
                data={editableRejection}
                isEditMode={rejectionModalEditMode}
                transplantDate={props.data.transplantDate}
                donorTyping={props.data.donorTyping}
                acuteAMRGradeList={props.lookupData.acuteAMRGradeList}
                acuteTCMRGradeList={props.lookupData.acuteTCMRGradeList}
                biopsyReasonList={props.lookupData.biopsyReasonList}
                iftaGradeList={props.lookupData.iftaGradeList}
                rejectionDSATypeList={props.lookupData.rejectionDSATypeList}
                rejectionTreatmentTypeList={props.lookupData.rejectionTreatmentTypeList}
                graftFailureReasonList={props.lookupData.graftFailureReasonList}
                onHide={handleCloseRejection}
                onSaveRejectionEpisodeDraft={props.onSaveRejectionEpisodeDraft}
                onCreateRejectionEpisode={props.onCreateRejectionEpisode}
                onUpdateRejectionEpisode={props.onUpdateRejectionEpisode}
                onDeleteRejectionEpisode={props.onDeleteRejectionEpisode}
                onUnDeleteRejectionEpisode={props.onUnDeleteRejectionEpisode}
            />
        </div>
    );
}

export default Content;
